import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { ConditionalWrapper } from '@/components/ConditionalWrapper';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ProfilePopoverContent } from './ProfilePopoverContent';

interface AuthButtonProps {
  isSidebarOpen: boolean;
}

export function AuthButton({ isSidebarOpen }: AuthButtonProps) {
  const { user } = useAuth();

  // Only show for authenticated users
  if (!user) return null;

  // Get user display info (simple token auth)
  const userName = user.name;
  const userInitials = userName
    ? userName
        .split(' ')
        .map((n: string) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : 'U';

  return (
    <Popover>
      <ConditionalWrapper
        condition={!isSidebarOpen}
        wrapper={(children) => (
          <Tooltip>
            <TooltipTrigger asChild>{children}</TooltipTrigger>
            <TooltipContent side="right" className="flex flex-col">
              <span className="font-semibold">Profile</span>
              <span className="text-xs text-muted-foreground">{userName}</span>
            </TooltipContent>
          </Tooltip>
        )}
      >
        <PopoverTrigger asChild>
          <Button
            variant={isSidebarOpen ? 'adam_dark' : 'adam_dark_collapsed_avatar'}
            className={`${isSidebarOpen ? 'w-full justify-start' : 'ml-[1px] h-[46px] w-[46px] p-0'}`}
          >
            <Avatar
              className={`${isSidebarOpen ? 'mr-2' : ''} h-[22px] w-[22px] min-w-[22px]`}
            >
              <AvatarFallback className="text-[10px] text-white">
                {userInitials}
              </AvatarFallback>
            </Avatar>
            {isSidebarOpen && (
              <span className="truncate">
                {userName || 'Profile'}
              </span>
            )}
          </Button>
        </PopoverTrigger>
      </ConditionalWrapper>
      <PopoverContent side="right" align="end" className="w-80">
        <ProfilePopoverContent
          userName={userName}
          userEmail={undefined}
          userAvatar={undefined}
          userInitials={userInitials}
        />
      </PopoverContent>
    </Popover>
  );
}
